import type { Project, ProjectFlagStage, ProjectReader } from '@/api/client';

/**
 * Отметки проекта (спек 2026-10-22-project-flag-stages-design): у каждой — стадия 0..3 (серая, красная, жёлтая, зелёная)
 * и комментарий. «Удалённая запись» — тоже поле проекта; сервер ведёт её за режимом чтецов.
 */
export type FlagField = 'project_remote' | 'project_phonogram' | 'project_dp' | 'project_pdf' | 'project_approved' | 'project_done';
export type FlagNoteField = `${FlagField}_note`;

export const FLAG_STAGES: readonly ProjectFlagStage[] = [0, 1, 2, 3];
/** Зелёная — «готово»: у «Удалённой записи» её выбор переводит всех чтецов в удалённую запись */
export const FLAG_STAGE_DONE: ProjectFlagStage = 3;

/** Для диктора (на экране цвета словами не называются): «Фонограмма: жёлтая» */
export const FLAG_STAGE_SPOKEN: Record<ProjectFlagStage, string> = { 0: 'нет', 1: 'красная', 2: 'жёлтая', 3: 'зелёная' };

/** Отметки по порядку вкладки и точек: имя — в ячейке вкладки, в подсказке точки и у попапа для диктора */
export const PROJECT_FLAGS: readonly { field: FlagField; name: string }[] = [
  { field: 'project_remote', name: 'Удалённая запись' },
  { field: 'project_phonogram', name: 'Фонограмма' },
  { field: 'project_dp', name: 'ЦП' },
  { field: 'project_pdf', name: 'PDF' },
  { field: 'project_approved', name: 'Согласовано' },
  { field: 'project_done', name: 'Сдан' },
];

export const flagNoteField = (field: FlagField): FlagNoteField => `${field}_note`;

/** Стадия из проекта: всё, кроме 1..3, — серая */
export function getFlagStage(project: Pick<Project, FlagField>, field: FlagField): ProjectFlagStage {
  const v = project[field];
  return v === 1 || v === 2 || v === 3 ? v : 0;
}

export interface FlagView {
  field: FlagField;
  name: string;
  stage: ProjectFlagStage;
  /** Комментарий; null — нет */
  note: string | null;
}

/** Шесть отметок проекта по порядку — УЗ, ФН, ЦП, PDF, СОГ, СД: вкладка шапки карточки и точки в строке списка */
export function getProjectFlags(project: Pick<Project, FlagField | FlagNoteField>): FlagView[] {
  return PROJECT_FLAGS.map(({ field, name }) => ({
    field,
    name,
    stage: getFlagStage(project, field),
    note: project[flagNoteField(field)]?.trim() || null,
  }));
}

/** Имя ячейки и точки для диктора: «Фонограмма: жёлтая, есть комментарий» */
export function flagSpokenLabel({ name, stage, note }: Pick<FlagView, 'name' | 'stage' | 'note'>): string {
  return `${name}: ${FLAG_STAGE_SPOKEN[stage]}${note ? ', есть комментарий' : ''}`;
}

/** Режим чтецов для «Удалённой записи»: удалённо все — зелёная, часть — жёлтая, никто (или чтецов нет) — серая */
function readersRemoteStage(readers: readonly Pick<ProjectReader, 'remote_recording'>[]): 0 | 2 | 3 {
  const remote = readers.filter((r) => r.remote_recording === true).length;
  if (remote === 0) return 0;
  return remote === readers.length ? 3 : 2;
}

/**
 * Оптимистичная правка чтецов с «Удалённой записью», какой её сделает сервер (nextRemoteStage в server/services/projectFlagInput):
 * режим «все / часть / никто» сменился — УЗ встаёт в 3 или 2, в «никто» гаснут 2 и 3; ручные серая и красная остаются.
 * Правка без чтецов или со своей стадией УЗ — как есть.
 */
export function withReadersRemoteStage(project: Project, optimistic: Partial<Project>): Partial<Project> {
  if (!optimistic.readers || optimistic.project_remote !== undefined) return optimistic;
  const before = readersRemoteStage(project.readers);
  const after = readersRemoteStage(optimistic.readers);
  if (before === after) return optimistic;
  const current = getFlagStage(project, 'project_remote');
  const next = after !== 0 ? after : current === 2 || current === 3 ? 0 : current;
  return next === current ? optimistic : { ...optimistic, project_remote: next };
}
